/**
 * Impact audio (#795) — plays an ability's committed `.mp3` when its damage
 * LANDS, replacing the old speculative cast-keypress SFX (which fired on a
 * press the sim might reject, and fired at the wrong moment for anything with
 * a cast time or travel time).
 *
 * The signal is the snapshot's cosmetic `damage` events (#48). Each one now
 * carries `ability` (#795) — the id of the ability responsible, or `null` for a
 * hit with no originating ability (auto-attack swing, DoT tick, trigger
 * reflect, wall/zone hazard). `null` → the generic procedural `"ability"` cue;
 * NEVER the previously-played ability's sound.
 *
 * BOUNDARY (#608): only the ability ID travels on the wire. The slug → `.mp3`
 * resolution happens here, client-side, via `resolveAbilitySound` — the sim and
 * the state hash know nothing about sound.
 *
 * KNOWN LIMITATION (#795 Q2, accepted): a hit fully eaten by a shield emits no
 * `damage` event at all (`effects.ts` gates on post-absorb `dealt > 0`), so a
 * fireball swallowed whole by Ice Barrier is SILENT. Keyed to `damage`
 * deliberately — a separate "something connected" signal is not worth a second
 * event kind. A whiffed skillshot is likewise silent (no cast cue any more,
 * and no impact), which is the intended consequence of the retarget.
 */

import type { AbilityId, EntityId, WireEvent } from "@pit/protocol";
import type { CueId } from "./cues.js";
import { resolveAbilitySound } from "./abilitySounds.js";

/** The generic fallback cue for an unattributed / sound-less hit. */
const FALLBACK_CUE: CueId = "ability";

/** The slice of `SoundEngineApi` impact playback needs. Keeping it narrow lets
 *  tests pass a two-method spy instead of a whole engine. */
export interface ImpactAudioPlayer {
  play(id: CueId): void;
  playFile(url: string): void;
}

/** "Can this viewer HEAR a hit on `target`?" — mount.ts passes the same
 *  `isHiddenFromViewer`-backed predicate combat text already uses (#422), so
 *  audio can never leak a hidden unit's presence either. A target missing from
 *  the snapshot is not audible. */
export type ImpactAudibility = (target: EntityId) => boolean;

/** One resolved playback request. `url` present ⇒ play that bundled file;
 *  absent ⇒ fire the procedural `cue`. */
interface Playback {
  readonly key: string;
  readonly url: string | null;
}

/**
 * Owns the per-snapshot impact playback cadence. Mirrors `CombatTextManager`'s
 * shape (constructed once by `mountRenderer`, fed once per snapshot ingest).
 *
 * WHY A CLASS AND NOT A BARE FUNCTION: the one bug this feature is prone to is
 * firing per RENDERED FRAME instead of per SNAPSHOT (a 60 Hz machine would
 * stutter the same sound ~6× per tick at 30 Hz snapshots). `lastTick` makes
 * that structurally impossible rather than relying on the call site staying in
 * the right place: re-ingesting a tick already ingested is a no-op.
 */
export class ImpactAudioManager {
  private lastTick: number | null = null;

  constructor(private readonly player: ImpactAudioPlayer) {}

  /**
   * Play this snapshot's impact audio. Call ONCE per snapshot, from the
   * snapshot-ingest path — never from the frame loop.
   *
   * Coalesced per snapshot by resolved sound: a 5-target blast is one
   * playback, not five overlapping copies of the same file. Distinct abilities
   * landing on the same tick each play once.
   */
  ingest(tick: number, events: readonly WireEvent[], isAudible: ImpactAudibility): void {
    if (this.lastTick === tick) return;
    this.lastTick = tick;
    const played = new Set<string>();
    for (const ev of events) {
      if (ev.kind !== "damage") continue;
      if (!isAudible(ev.target)) continue;
      const pb = resolve(ev.ability);
      if (played.has(pb.key)) continue;
      played.add(pb.key);
      if (pb.url === null) this.player.play(FALLBACK_CUE);
      else this.player.playFile(pb.url);
    }
  }
}

/** Ability id → the playback to request. Unattributed (`null`), an unknown id,
 *  or an ability with no committed `.mp3` all fall back to the generic cue —
 *  the same rip-out contract `resolveAbilitySound` documents. */
function resolve(ability: AbilityId | null): Playback {
  if (ability === null) return { key: FALLBACK_CUE, url: null };
  const sound = resolveAbilitySound(ability);
  return sound === null ? { key: FALLBACK_CUE, url: null } : { key: sound.slug, url: sound.url };
}
